import { formatSizeLabel, getProductSizeValues, getSizeValues, type SizeLike } from './sizeOptions';
import {
  CartItemLike,
  MaterialDataLike,
  CartMaterial,
  CartItemValidationIssue
} from '../components/interfaces';
import { getVariantStockQuantity, isVariantAvailable } from './variantInventory';



const formatVariantName = (materialName: string, selectedSize?: string | null) =>
  selectedSize ? `${materialName} size ${formatSizeLabel(selectedSize)}` : materialName;

export const getMaterialForCartItem = (
  item: CartItemLike,
  materialData?: MaterialDataLike | null
): CartMaterial | null => {
  const selectedMaterial = item.selectedMaterial || '';

  if (materialData && materialData.selectedMaterial === selectedMaterial) {
    return {
      materialName: selectedMaterial,
      stockQuantity: materialData.stockQuantity,
      isAvailable: materialData.isAvailable,
      sizes: materialData.sizes,
      variantInventories: materialData.variantInventories,
    };
  }

  return item.product?.materialPrices?.find(
    (material) => material.materialName === selectedMaterial
  ) || null;
};

export const getAvailableSizesForCartItem = (item: CartItemLike, material?: CartMaterial | null) =>
  getProductSizeValues(
    item.product?.sizes,
    material ? [material] : item.product?.materialPrices || [],
    item.selectedMaterial
  );

export const getSizeAvailabilityMessage = ({
  material,
  productId,
  selectedSize,
  quantity = 1,
}: {
  material: CartMaterial;
  productId?: string | null;
  selectedSize?: string | null;
  quantity?: number | null;
}) => {
  const materialName = material.materialName || 'This material';
  const variantName = formatVariantName(materialName, selectedSize);

  if (!isVariantAvailable(material, selectedSize, productId)) {
    return `${variantName} is unavailable.`;
  }

  const stockQuantity = getVariantStockQuantity(material, selectedSize, productId);

  if (typeof stockQuantity === 'number' && stockQuantity <= 0) {
    return `${variantName} is out of stock.`;
  }

  if (
    typeof stockQuantity === 'number' &&
    typeof quantity === 'number' &&
    quantity > stockQuantity
  ) {
    return `Only ${stockQuantity} item(s) available for ${variantName}.`;
  }

  return '';
};

export const getFirstValidSizeForMaterial = ({
  material,
  productSizes,
  productId,
  quantity = 1,
}: {
  material: CartMaterial;
  productSizes?: SizeLike[] | null;
  productId?: string | null;
  quantity?: number | null;
}) => {
  const sizes = getSizeValues(material.sizes?.length ? material.sizes : productSizes);

  return sizes.find((size) =>
    !getSizeAvailabilityMessage({
      material,
      productId,
      selectedSize: size,
      quantity,
    })
  ) || '';
};

export const getNextValidSizeForMaterial = ({
  material,
  productSizes,
  currentSize,
  productId,
  quantity = 1,
}: {
  material: CartMaterial;
  productSizes?: SizeLike[] | null;
  currentSize?: string | null;
  productId?: string | null;
  quantity?: number | null;
}) => {
  const materialSizeValues = getSizeValues(material.sizes);
  const availableSizes = materialSizeValues.length
    ? materialSizeValues
    : getSizeValues(productSizes);

  if (!availableSizes.length) {
    return '';
  }

  const currentSizeValue = currentSize || '';
  const currentSizeIssue = currentSizeValue
    ? getSizeAvailabilityMessage({
      material,
      productId,
      selectedSize: currentSizeValue,
      quantity,
    })
    : '';

  if (currentSizeValue && availableSizes.includes(currentSizeValue) && !currentSizeIssue) {
    return currentSizeValue;
  }

  return getFirstValidSizeForMaterial({
    material,
    productSizes,
    productId,
    quantity,
  });
};

export const getCartItemValidationIssue = (
  item: CartItemLike,
  materialData?: MaterialDataLike | null
): CartItemValidationIssue | null => {
  if (!item.selectedMaterial) {
    return {
      itemId: item.id,
      field: 'material',
      message: 'Please select a material.',
    };
  }

  const material = getMaterialForCartItem(item, materialData);

  if (!material) {
    return {
      itemId: item.id,
      field: 'material',
      message: `${item.selectedMaterial} is no longer available for this product.`,
    };
  }

  const availableSizes = getAvailableSizesForCartItem(item, material);
  const selectedSize = item.selectedSize || '';

  if (availableSizes.length > 0) {
    if (!selectedSize) {
      return {
        itemId: item.id,
        field: 'size',
        message: 'Please select a size.',
      };
    }

    if (!availableSizes.includes(selectedSize)) {
      return {
        itemId: item.id,
        field: 'size',
        message: `${material.materialName || item.selectedMaterial} is not available in size ${formatSizeLabel(selectedSize)}.`,
      };
    }
  }

  const stockMessage = getSizeAvailabilityMessage({
    material,
    productId: item.product?.id,
    selectedSize,
    quantity: item.quantity || 1,
  });

  if (stockMessage) {
    return {
      itemId: item.id,
      field: 'stock',
      message: stockMessage,
    };
  }

  return null;
};

export const getCartValidationIssues = (
  cartItems: CartItemLike[],
  materialDataByItemId: Record<string, MaterialDataLike | undefined>
) =>
  cartItems
    .map((item) => getCartItemValidationIssue(item, materialDataByItemId[item.id]))
    .filter((issue): issue is CartItemValidationIssue => Boolean(issue));
